import { shallowReactive, toRefs, watch } from 'vue';
import DocumentsModal from '@/components/documents/DocumentsModal.vue';
import { Exercise } from '@/model/app/Exercise';
import useModal from '@/services/useModal';
import useExercises from '@/services/useExercises';
import { taskIdDefault } from '@/types/enums/WritingStory.enum';
import useLocalization from '@/services/useLocalization';
import { useRouter } from 'vue-router';

interface useWriteStoryArguments {
  genreSlug: string;
  taskId?: string;
}

interface WriteStoryState {
  exercises: Exercise[];
  genreSlug: string;
  taskId: string;
  hasExercises: boolean;
}

const useWriteStory = ({ genreSlug, taskId = taskIdDefault }: useWriteStoryArguments) => {
  const { open } = useModal();
  const { generateExerciseLink, generateVersion, exercises, castTaskId } = useExercises();
  const { locale } = useLocalization();
  const router = useRouter();

  const state: WriteStoryState = shallowReactive({
    exercises: [],
    genreSlug: genreSlug,
    taskId: taskId,
    hasExercises: false,
  });

  const filterExercises = (exercises: Exercise[]) => {
    if (!exercises?.length) return;

    const data = exercises.filter((doc) => {
      return (
        (!state.genreSlug || doc.genre === state.genreSlug) &&
        (!state.taskId || doc.task === castTaskId(state.taskId)) &&
        locale.value === doc.language
      );
    });

    state.exercises = data;
    state.hasExercises = !!data.length;
  };

  watch(exercises, (exercises) => {
    filterExercises(exercises);
  });

  watch(locale, () => filterExercises(exercises.value));

  filterExercises(exercises.value);

  const openDocumentsModal = (genre: string) => {
    open(DocumentsModal, {
      genre,
    });
  };

  const newExerciseLink = async (version?: number) => {
    const versionParam = version ?? (await generateVersion(state.genreSlug, state.taskId));

    return generateExerciseLink(state.genreSlug, versionParam, state.taskId);
  };

  const createNewStory = async () =>
    await router.push({
      path: await newExerciseLink(),
    });

  const continueStory = async () => {
    if (state.exercises.length === 1) {
      const exercise = state.exercises[0];
      router.push({ path: await newExerciseLink(exercise.version) });
    }
    if (state.exercises.length > 1) {
      openDocumentsModal(state.genreSlug);
    }
  };

  const updateWriteStoryArguments = ({ genreSlug, taskId = taskIdDefault }: useWriteStoryArguments) => {
    state.genreSlug = genreSlug;
    state.taskId = taskId;

    filterExercises(exercises.value);
  };

  return {
    ...toRefs(state),
    createNewStory,
    continueStory,
    newExerciseLink,
    updateWriteStoryArguments,
  };
};

export default useWriteStory;
